import PopupModal from '$components/PopupModal'
import { useGlobalServiceUserService } from '$hooks/services'
import MainHeader from '$layouts/UserProfileLayout/components/MainHeader'
import { description, flexColumn, flexRow } from '$styles/common.css'
import { Checkbox } from '@genie-fintech/ui/components'
import { body, footnote, title } from '@genie-fintech/ui/style/typography'
import { useBoolean, useMount, useRequest } from 'ahooks'
import {
  Mail,
  QrCode,
  ShieldCheck,
  ShieldClose,
  Smartphone
} from 'lucide-react'
import { useCallback } from 'react'
import { innerContainer, shield } from './styles.css'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import Loading from '$components/Loading'
import { useProfileDataStore } from '$layouts/UserProfileLayout/hooks/useProfileDataStore'
import {
  innerMainContent,
  mainContent
} from '$layouts/UserProfileLayout/styles.css'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import OTPSetting from '../../../components/OTPSetting'
import { status } from '@genie-fintech/ui/style/element'
import { getServiceUserMfaMethods } from '$services/api'
import Empty from '$components/Empty'
import { getPhone } from '$utils'
import { format } from 'date-fns'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { usePermissions } from '$hooks/actions'
import { PERMISSIONS } from '$app/constants/common'

const { colors } = themeVars

const GlobalServiceUserMFASetting = () => {
  const [open, { setTrue, setFalse }] = useBoolean()

  const isMounted = useIsMounted()

  const { checkPermissions } = usePermissions()

  const CAN_EDIT = checkPermissions([PERMISSIONS.USER_MFA_SETTING_EDIT])

  const {
    fetchServiceUserMFASetting,
    fetchingServiceUserMFASetting,
    user_mfa,
    enforceMFAAsync,
    enforcingMFA,
    fetchMFAOTP,
    fetchingMFAOTP,
    mfaOTP
  } = useGlobalServiceUserService()

  const {
    data: mfaMethodsData,
    loading: fetchingMfaMethods,
    run: fetchServiceUserMFAMethods
  } = useRequest(getServiceUserMfaMethods, { manual: true })

  const {
    id: userId,
    email,
    phone_code,
    phone_no
  } = useProfileDataStore(state => state.profile) ?? {}

  const { mfa_enforced = false, mfa_enabled = false } = user_mfa ?? {}

  const fetchMFAData = useCallback(() => {
    if (!userId) return
    fetchServiceUserMFASetting({ userId })
    fetchServiceUserMFAMethods(userId)
  }, [userId, fetchServiceUserMFASetting, fetchServiceUserMFAMethods])

  const onGetMFAOTP = useCallback(() => {
    if (!userId) return
    fetchMFAOTP({ userId })
  }, [userId, fetchMFAOTP])

  useMount(fetchMFAData)

  useBreadcrumb([{ name: 'Two-Factor Authentication' }])

  const onMFACheckboxChange = useCallback(() => {
    setTrue()
  }, [setTrue])

  const onConfirm = useCallback(() => {
    if (!userId) return
    enforceMFAAsync(userId, { enforce_mfa: !mfa_enforced })
      .then(fetchMFAData)
      .then(setFalse)
  }, [userId, mfa_enforced, enforceMFAAsync, fetchMFAData, setFalse])

  const { registered_factors = [] } = mfaMethodsData?.data ?? {}

  if (!user_mfa || !isMounted) return <Loading />

  const isEmptyMFAMethod = !registered_factors.length && !fetchingMfaMethods

  const isTurnedOnMFA = mfa_enforced || mfa_enabled

  return (
    <>
      <PopupModal open={open} onConfirm={onConfirm} onClose={setFalse} />

      <MainHeader
        title="TWO-FACTOR AUTHENTICATION"
        desc="It provides more than one form of authentication to access an account or application."
      />

      <article
        className={mainContent}
        style={{ borderBottom: `1px solid ${colors.neutral[10]}` }}
      >
        <article className={innerContainer}>
          <article className={flexColumn} style={{ flex: 1, gap: 4 }}>
            <p className={title.two}>Enforce TWO-FACTOR AUTHENTICATION</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              Prevent hackers from accessing your account with an additional
              layer of security.
            </p>
          </article>

          <Checkbox
            label="Enforce 2FA"
            boxProps={{
              checked: mfa_enforced,
              onCheckedChange: onMFACheckboxChange
            }}
            styleVariants={{ bordered: true, padded: true }}
            disabled={
              enforcingMFA || fetchingServiceUserMFASetting || !CAN_EDIT
            }
          />

          <article
            className={shield}
            data-active={trueOrUndefined(mfa_enforced)}
          >
            {mfa_enforced ? (
              <ShieldCheck size={24} />
            ) : (
              <ShieldClose size={24} />
            )}
          </article>
        </article>
      </article>

      <article
        className={mainContent}
        style={{ borderBottom: `1px solid ${colors.neutral[10]}` }}
      >
        <article className={innerMainContent} style={{ gap: 20 }}>
          <article
            className={flexRow}
            style={{ gap: 8, alignItems: 'flex-start' }}
          >
            <article className={flexColumn} style={{ flex: 1, gap: 4 }}>
              <p className={title.two}>MFA METHODS</p>
              <p className={description}>
                Make sure you can access your account by keeping this
                information up to date and adding more sign-in options.
              </p>
            </article>

            <span className={status({ type: 'faint', style: 'rounded' })}>
              {isTurnedOnMFA ? 'Turned on 2FA' : 'Turned off 2FA'}
            </span>
          </article>

          {isEmptyMFAMethod && <Empty />}

          {registered_factors.map((mfa_factor, index) => {
            const Icon = (() => {
              if (mfa_factor.type === 'email_otp') return Mail
              if (mfa_factor.type === 'totp') return QrCode
              return Smartphone
            })()

            const value = (() => {
              if (mfa_factor.type === 'email_otp') return email
              if (mfa_factor.type === 'sms_otp')
                return getPhone({
                  phoneCode: phone_code,
                  phoneNo: phone_no
                })
              return ''
            })()

            const date = mfa_factor.updated_at || mfa_factor.created_at

            return (
              <>
                {index > 0 && (
                  <hr style={{ border: `1px solid ${colors.neutral[10]}` }} />
                )}

                <article
                  key={mfa_factor.id}
                  className={flexRow}
                  style={{ padding: '32px 0', gap: 16 }}
                >
                  <Icon size={32} style={{ color: colors.neutral[60] }} />
                  <article className={flexColumn} style={{ flex: 1, gap: 2 }}>
                    <article className={flexRow} style={{ gap: 8 }}>
                      <p
                        className={title.five}
                        style={{ color: colors.text.light }}
                      >
                        {mfa_factor.name}
                      </p>

                      {mfa_factor.is_preferred && (
                        <span
                          className={status({
                            type: 'faint',
                            size: 'small',
                            kind: 'primary'
                          })}
                        >
                          Preferred Method
                        </span>
                      )}
                    </article>

                    {!!value && (
                      <p
                        className={body.three}
                        style={{ color: colors.neutral[60] }}
                      >
                        {value}
                      </p>
                    )}
                  </article>

                  {date && (
                    <p className={description}>
                      Added at {format(date, 'dd MMM yyyy')}
                    </p>
                  )}
                </article>
              </>
            )
          })}
        </article>
      </article>

      <article className={mainContent}>
        <article
          className={innerMainContent}
          style={{ gap: 40, paddingBottom: 80 }}
        >
          <OTPSetting
            title="Requested 2fa OTP"
            otpData={mfaOTP}
            loading={fetchingMFAOTP}
            onGetOTP={onGetMFAOTP}
            disabled={!CAN_EDIT}
          />
        </article>
      </article>
    </>
  )
}

export default GlobalServiceUserMFASetting
